

import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  IconButton,
  Divider,
  Badge,
  Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { getAuthenticatorUrl } from "../../Utils/webOrigin";

// Added professional placeholder images for a better visual experience
const productsData = [
  { id: 6, name: "Salvus Passport Cover", price: 1999, image: "https://thepeppystore.in/cdn/shop/products/WhatsAppImage2022-06-06at3.45.47PM.jpg?v=1713617278&width=800" },
  { id: 4, name: "Salvus Smart Ring", price: 9999, image: "https://hifuturegroup.com/cdn/shop/articles/Blog_b0b08fc8-658f-4d25-97f0-af9ec54f0538.jpg?v=1688198208&width=1400" },
  { id: 2, name: "Salvus Wallet", price: 4999, image: "https://images.unsplash.com/photo-1627123424574-724758594e93?auto=format&fit=crop&w=400&q=80" },
  { id: 1, name: "Laptop Pro", price: 60000, image: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=400&q=80" },
  { id: 3, name: "Noise Cancelling Headphones", price: 2000, image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=400&q=80" },
  { id: 5, name: "Mechanical Keyboard", price: 1500, image: "https://images.unsplash.com/photo-1595225476474-87563907a212?auto=format&fit=crop&w=400&q=80" },
  { id: 7, name: "Wireless Mouse", price: 800, image: "https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?auto=format&fit=crop&w=400&q=80" }
];

const Dashboard = () => {
  const [cart, setCart] = useState([]);

  const addToCart = (product) => {
    setCart((prev) => {
      const exists = prev.find((item) => item.id === product.id);
      if (exists) {
        return prev.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (id, type) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.id === id) {
            const qty = type === "inc" ? item.quantity + 1 : item.quantity - 1;
            return { ...item, quantity: qty };
          }
          return item;
        })
        .filter((item) => item.quantity > 0)
    );
  };

  const totalAmount = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handlePay = () => {
    localStorage.setItem("cart", JSON.stringify(cart));
    localStorage.setItem("totalAmount", totalAmount);

    setTimeout(() => {
      window.location.href = getAuthenticatorUrl();
    }, 800);
  };

  return (
    <Box sx={{ backgroundColor: "#f4f6f8", minHeight: "100vh", pb: 5 }}>
      {/* Cool Gradient Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
          color: "white",
          py: 4,
          px: { xs: 2, md: 6 },
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          mb: 5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottomLeftRadius: 24,
          borderBottomRightRadius: 24,
        }}
      >
        <Typography variant="h3" fontWeight="800" sx={{ letterSpacing: 1 }}>
          SEC@R <Box component="span" sx={{ color: "#00d2ff" }}>Smart Store</Box>
        </Typography>

        <Badge badgeContent={cart.length} color="error" overlap="circular">
          <Avatar sx={{ bgcolor: "white", color: "#1e3c72", width: 56, height: 56, boxShadow: 3 }}>
            <ShoppingCartIcon fontSize="large" />
          </Avatar>
        </Badge>
      </Box>

      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Products Section */}
          <Grid item xs={12} md={8}>
            <Box display="flex" alignItems="center" mb={3}>
              <ShoppingBagIcon sx={{ color: "#1e3c72", mr: 1, fontSize: 32 }} />
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                Featured Products
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {productsData.map((product) => (
                <Grid item xs={12} sm={6} lg={4} key={product.id}>
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={product.image}
                      alt={product.name}
                      sx={{ borderTopLeftRadius: 16, borderTopRightRadius: 16 }}
                    />
                    <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {product.name}
                        </Typography>
                        <Typography variant="h5" color="primary" fontWeight="bold" sx={{ mb: 2 }}>
                          ₹ {product.price.toLocaleString()}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        fullWidth
                        sx={{
                          borderRadius: 2,
                          py: 1.2,
                          textTransform: "none",
                          fontSize: "1rem",
                          fontWeight: "bold",
                          background: "linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)",
                          "&:hover": {
                            background: "linear-gradient(90deg, #152b52 0%, #1e3c72 100%)",
                          }
                        }}
                        onClick={() => addToCart(product)}
                      >
                        Add to Cart
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Sticky Cart Section */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: "sticky", top: 24 }}>
              <Typography variant="h4" fontWeight="bold" mb={3} color="text.primary">
                Your Cart
              </Typography>

              <Card sx={{ borderRadius: 4, boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
                <CardContent sx={{ p: 3 }}>
                  {cart.length === 0 ? (
                    <Box textAlign="center" py={5}>
                      <ShoppingCartIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" color="text.secondary">
                        Your cart is looking empty.
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        Add some awesome items to get started!
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {cart.map((item) => (
                        <Box key={item.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar src={item.image} variant="rounded" sx={{ width: 50, height: 50 }} />
                            <Box>
                              <Typography fontWeight="bold" variant="body1">{item.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                ₹ {item.price.toLocaleString()}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#f0f2f5", borderRadius: 2 }}>
                            <IconButton size="small" onClick={() => updateQuantity(item.id, "dec")} color="error">
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography fontWeight="bold" sx={{ px: 1 }}>{item.quantity}</Typography>
                            <IconButton size="small" onClick={() => updateQuantity(item.id, "inc")} color="primary">
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}

                      <Divider sx={{ my: 3 }} />

                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h6" color="text.secondary">Total</Typography>
                        <Typography variant="h5" fontWeight="900" color="primary">
                          ₹ {totalAmount.toLocaleString()}
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        sx={{
                          py: 1.5,
                          borderRadius: 3,
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          textTransform: "none",
                          background: "linear-gradient(45deg, #00b09b, #96c93d)",
                          boxShadow: "0 4px 14px rgba(0, 176, 155, 0.4)",
                          "&:hover": {
                            background: "linear-gradient(45deg, #008f7e, #7aa832)",
                          }
                        }}
                        onClick={handlePay}
                      >
                        Proceed to Checkout
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;